// Generic function to filter options by any property
export function getFilteredOptions(
  options: any[],
  filterOptions: any[],
  propertyName: string,
  propertyValue: string,
) {
  if (!propertyValue || !options || propertyValue.startsWith("No")) {
    return [];
  }

  if (filterOptions.length <= 1) {
    return options;
  }

  return options
    .map((group: any) => {
      if (Array.isArray(group.options)) {
        const filtered = group.options.filter(
          (option: any) => option[propertyName] === propertyValue,
        );
        return { ...group, options: filtered };
      }

      return group;
    })
    .filter((group: any) => {
      if (Array.isArray(group.options)) {
        return group.options.length > 0;
      }
      return true;
    });
}

/**
 * Parses a composite entity key into its components
 * Format: "name||code||registrationNumber" or "name||code"
 */
const parseEntityCompositeKey = (compositeKey: string) => {
  if (!compositeKey?.includes("||")) {
    return { name: compositeKey, code: "", registrationNumber: "" };
  }

  const parts = compositeKey.split("||");
  return {
    name: parts[0] || "",
    code: parts[1] || "",
    registrationNumber: parts[2] || "",
  };
};

/**
 * Parses entity code format: "code" or "code - registration number"
 */
const parseEntityCode = (entityCode: string) => {
  const parts = entityCode.split(" - ");
  return {
    code: parts[0],
    registrationNumber: parts[1], // undefined if not present
  };
};

/**
 * Builds a composite entity key from components
 * Accepts entityCode which may be in format "code" or "code - registration number"
 * and parses it internally before building the composite key
 */
export const buildEntityCompositeKey = (name: string, entityCode: string) => {
  const { code, registrationNumber } = parseEntityCode(entityCode);
  return registrationNumber
    ? `${name}||${code}||${registrationNumber}`
    : `${name}||${code}`;
};

/**
 * Extracts entity code from composite key
 */
export const getEntityCodeFromCompositeKey = (compositeKey: string) => {
  const { code, registrationNumber } = parseEntityCompositeKey(compositeKey);
  return registrationNumber ? `${code} - ${registrationNumber}` : code;
};

/**
 * Extracts entity name from composite key
 */
export const getEntityNameFromEntityCompositeKey = (compositeKey: string) => {
  const { name } = parseEntityCompositeKey(compositeKey);
  return name;
};

// Make sure the Ui content is not the dummy value.
export const hasRealUiContent = (uiContent: any): boolean => {
  if (!uiContent) return false;

  const optionKeys = [
    "countryOptions",
    "entityOptions",
    "governmentEntityOptions",
  ];

  for (const key of optionKeys) {
    const options = uiContent[key];
    if (options && options.length > 0) {
      const hasDummyData = options.some(
        (option: any) =>
          option.value?.startsWith("No ") || option.label?.startsWith("No "),
      );
      if (hasDummyData) {
        return false;
      }
    }
  }

  return true;
};

/**
 * Gets entity code from entity value (supports both composite key and entity long name)
 * extracts code directly if composite key
 * looks up by label if long name
 * NOTE: Long name (label) is not guaranteed to be unique in entityOptions!
 * When several options share a name, a registrationNumber (GSTIN) disambiguates;
 * otherwise the first match is returned.
 */
export const getEntityCodeFromLongName = (
  entityValue: string,
  uiContent: any,
  registrationNumber?: string,
) => {
  if (!entityValue || !uiContent?.entityOptions) {
    return "";
  }

  // Check if it's a composite key format
  if (entityValue.includes("||")) {
    return getEntityCodeFromCompositeKey(entityValue);
  }

  // entity value is just the name - find by label
  const matches = uiContent.entityOptions.filter(
    (option: any) => option.label === entityValue,
  );
  // Names are not unique; disambiguate duplicates by the extracted GSTIN when given
  if (registrationNumber && matches.length > 1) {
    const byRegistration = matches.find(
      (option: any) =>
        parseEntityCode(option.description ?? "").registrationNumber ===
        registrationNumber,
    );
    if (byRegistration) {
      return byRegistration.description ?? "";
    }
  }
  return matches[0]?.description ?? "";
};

export const getCurrencySignFromCountry = (country: string, uiContent: any) => {
  // Fallback - we can remove once global country/state uicontent is available for all teams
  const COUNTRY_TO_CURRENCY_MAPPING: Record<string, string> = {
    US: "$",
    BR: "R$",
    CA: "C$",
    IN: "₹",
    DE: "€",
    PR: "$",
    EU: "€",
    APAC: "",
  };

  const shortCountryName = getShortNameFromLong(country);

  let mapping = COUNTRY_TO_CURRENCY_MAPPING;

  if (
    uiContent?.countryToCurrencyMap &&
    Object.keys(uiContent.countryToCurrencyMap).length > 0
  ) {
    mapping = uiContent.countryToCurrencyMap;
  }

  return mapping[shortCountryName] ?? "$";
};

/** ISO 4217 code for a country from the global country content; undefined for regions or before it loads. */
export const getCurrencyCodeFromCountry = (
  country: string,
  uiContent: any,
): string | undefined =>
  uiContent?.countryToCurrencyCodeMap?.[getShortNameFromLong(country)] ||
  undefined;

/** FinTech national-bank rate type for a country (e.g. NB_TR); undefined when it has none. */
export const getNationalBankRateTypeFromCountry = (
  country: string,
  uiContent: any,
): string | undefined =>
  uiContent?.countryToNationalBankRateTypeMap?.[
    getShortNameFromLong(country)
  ] || undefined;

const longToShortMapping = {
  "United States": "US",
  Brazil: "BR",
  Canada: "CA",
  India: "IN",
  Germany: "DE",
  "Puerto Rico": "PR",
  "European Union": "EU",
  "Asia-Pacific": "APAC",
};

export const getShortNameFromLong = (name: string): string => {
  return longToShortMapping[name as keyof typeof longToShortMapping] || name;
};

export const getLongNameFromShort = (name: string): string => {
  const longNameEntry = Object.entries(longToShortMapping).find(
    ([_longName, shortCode]) => shortCode === name,
  );
  return longNameEntry ? longNameEntry[0] : name;
};
